import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { IDeviceMonitoringData } from '../device-monitoring/devicemonitoring.component';

@Component({
  selector: 'dcc-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
})
export class TimelineComponent implements OnChanges, OnInit {
  @Input() serialNumber = '';
  @Input() startDate: Date | undefined;
  @Input() endDate: Date | undefined;
  @Input() monitoringData: IDeviceMonitoringData | undefined;

  @Output() dissociated = new EventEmitter<void>();
  @Output() dissociationConfirmed = new EventEmitter<void>();

  hoverBarActive = false;
  hoverBarX = 0;
  hoverBarTooltipX = 0;
  hoverBarTime = 0;

  hubTimelineDataTypes: string[] = [
    'ANALOG_AUX_1',
    'ANALOG_AUX_2',
    'ANALOG_AUX_3',
    'ANALOG_AUX_4',
  ];
  camTimelineDataTypes: string[] = [
    'FORWARD',
    'REAR',
    'AUDIO',
    'ACCEL',
    'GYRO',
    'GPS',
    'VNET',
  ];
  timelineDataTypes: string[] = [];
  ecmDataTypes: string[] = [
    'SPEED_KPH',
    'ENGINE_SPEED',
    'ENGINE_THROTTLE_POSITION',
    'ENGINE_HOURS',
    'ODOMETER',
    'DTC',
    'MIL',
  ];
  // Battery section rows (one per device.batterybackupstats / device.batteryissue
  // parameter exposed by the battery API). Order here is the render order.
  batteryDataTypes: string[] = [
    'Voltage',
    'Charger On/Off',
    'Battery Warnings',
    'Internal Resistance',
    'Recharge Events',
    'RTC-loss',
    'Battery Health',
  ];

  errorMessage = '';
  segmentsByType: Map<string, IPlaceable[]> = new Map<string, IPlaceable[]>();
  timelineType: Map<string, IPlaceable[]> = new Map<string, IPlaceable[]>();
  ecmType: Map<string, IPlaceable[]> = new Map<string, IPlaceable[]>();
  batteryType: Map<string, IPlaceable[]> = new Map<string, IPlaceable[]>();
  checkInDates: string[] = [];

  // keyvalue pipe comparator that preserves Map insertion order (the default
  // sorts keys alphabetically). Used to keep the Battery rows in declared order.
  readonly originalOrder = (): number => 0;

  // 230 is width to the end of the data type header column
  private widthToEndOfDataHeadersOnXAxis = 230;
  private widthToEndOfDataHeadersInGridElement = 240;
  private marginOfTimelineGridElement = 15;
  private widthOfTimelineGrid = 0;

  constructor(private el: ElementRef) {}

  // Feature-flagged the same way as the Device Battery insights tab
  // (app-routing.module.ts → enableBatteryView) and the Device Battery
  // metadata section, so the new battery timeline row stays hidden until
  // the toggle is explicitly enabled on the URL.
  // eslint-disable-next-line @typescript-eslint/member-ordering
  get enableBatteryView(): boolean {
    return new URLSearchParams(window.location.search).get('enableBatteryView') === 'true';
  }

  async ngOnChanges(): Promise<void> {
    await this.renderData();
  }

  async ngOnInit(): Promise<void> {
    await this.renderData();
  }

  onHover(event: MouseEvent) {
    this.setWidthOfTimelineGrid();

    //Fixed misalignment of hoverbar from cursor with 10px gap between the cursor and hover bar.
    const container = this.el.nativeElement.querySelector(
      '.timeline__content__row-container',
    );
    const rect = container.getBoundingClientRect();
    const relativeX = event.clientX - rect.left;
    const timelineStart = this.widthToEndOfDataHeadersOnXAxis;
    const timelineEnd = timelineStart + this.widthOfTimelineGrid;

    this.hoverBarActive =
      relativeX >= timelineStart && relativeX <= timelineEnd;

    if (this.hoverBarActive) {
      this.hoverBarX = relativeX + 10;
      const tooltipElement = this.el.nativeElement.querySelector(
        '.timeline__content__hover-bar-tooltip',
      );
      const tooltipWidth = tooltipElement ? tooltipElement.offsetWidth : 100;
      if (relativeX - tooltipWidth / 2 < timelineStart) {
        this.hoverBarTooltipX = timelineStart;
      } else if (relativeX + tooltipWidth / 2 > timelineEnd) {
        this.hoverBarTooltipX = timelineEnd - tooltipWidth;
      } else {
        this.hoverBarTooltipX = relativeX - tooltipWidth / 2;
      }
      const positionInTimelineGrid = relativeX - timelineStart;
      this.hoverBarTime = this.findHoverTime(positionInTimelineGrid);
    }
  }

  private async renderData(): Promise<void> {
    if (!this.monitoringData?.timelineData?.length) {
      this.errorMessage = 'No data found for this serial number.';
      return;
    }
    const dataToDisplay = this.selectDataWithinTimeSpan(
      this.monitoringData?.timelineData,
    );

    if (!dataToDisplay.length) {
      this.errorMessage =
        'No data found for this serial number in this timeframe.';
      return;
    }
    const placeableData = this.calculateDataPlacement(dataToDisplay);
    const isHub = this.serialNumber.toLowerCase().startsWith('am');
    this.groupDataByType(placeableData, isHub);
  }

  private groupDataByType(data: IPlaceable[], isHub: boolean): void {
    if (isHub) {
      this.timelineDataTypes = this.hubTimelineDataTypes;
    } else {
      this.timelineDataTypes = this.camTimelineDataTypes;
    }
    // Reset the battery map on every render so stale segments from a prior
    // ngOnChanges call don't accumulate. Seed in fixed order so the Battery
    // section always shows every parameter row even when it has no data.
    // Only seed when the feature flag is on; otherwise batteryType stays empty
    // and the Battery section in the template is correctly hidden.
    this.batteryType.clear();
    if (this.enableBatteryView) {
      this.batteryDataTypes.forEach((type) => this.batteryType.set(type, []));
    }
    data.forEach((segment) => {
      segment.dataTypes.forEach((type) => {
        if (
          !this.segmentsByType.get(type) &&
          !this.timelineType.get(type) &&
          !this.ecmType.get(type) &&
          !this.batteryType.get(type)
        ) {
          if (this.timelineDataTypes.includes(type)) {
            this.timelineType.set(type, []);
          } else if (this.ecmDataTypes.includes(type)) {
            this.ecmType.set(type, []);
          } else if (this.enableBatteryView && this.batteryDataTypes.includes(type)) {
            this.batteryType.set(type, []);
          } else {
            this.segmentsByType.set(type, []);
          }
        }
        if (this.timelineDataTypes.includes(type)) {
          this.timelineType.get(type)!.push({
            dataTypes: [type],
            left: segment.left,
            width: segment.width,
            hint: segment.hint,
            styles: segment.styles,
          });
        } else if (this.ecmDataTypes.includes(type)) {
          if (!isHub) {
            this.ecmType.get(type)!.push({
              dataTypes: [type],
              left: segment.left,
              width: segment.width,
              hint: segment.hint,
              styles: segment.styles,
            });
          }
        } else if (this.enableBatteryView && this.batteryDataTypes.includes(type)) {
          this.batteryType.get(type)!.push({
            dataTypes: [type],
            left: segment.left,
            width: segment.width,
            hint: segment.hint,
            styles: segment.styles,
          });
        } else {
          this.segmentsByType.get(type)!.push({
            dataTypes: [type],
            left: segment.left,
            width: segment.width,
            hint: segment.hint,
            styles: segment.styles,
          });
        }
      });
    });
    this.timelineDataTypes.forEach((type) => {
      if (!this.timelineType.has(type)) {
        this.timelineType.set(type, []);
      }
    });
    if (!isHub) {
      this.ecmDataTypes.forEach((type) => {
        if (!this.ecmType.has(type)) {
          this.ecmType.set(type, []);
        }
      });
      if (!this.segmentsByType.get('Ignition')) {
        this.segmentsByType.set('Ignition', []);
      }
    }
  }

  private selectDataWithinTimeSpan(data: ITimelineData[]): ITimelineData[] {
    return data.reduce((segmentsInRange, dataElement) => {
      const segmentStartDate = new Date(dataElement.start);
      const segmentEndDate =
        dataElement.end === undefined
          ? new Date(dataElement.start)
          : new Date(dataElement.end);

      const segmentIntersectsWithTimespan =
        segmentStartDate < this.endDate! && segmentEndDate > this.startDate!;
      if (!segmentIntersectsWithTimespan) {
        return segmentsInRange;
      }

      // trim down segment fragments that lie outside the selected time-span
      segmentsInRange.push({
        dataTypes: dataElement.dataTypes,
        start:
          segmentStartDate < this.startDate!
            ? this.startDate!
            : dataElement.start,
        end: segmentEndDate < this.endDate! ? dataElement.end : this.endDate!,
        hint: dataElement.hint,
        styles: dataElement.styles,
      });
      return segmentsInRange;
    }, [] as ITimelineData[]);
  }

  private calculateDataPlacement(data: ITimelineData[]): IPlaceable[] {
    if (!this.startDate || !this.endDate) {
      throw new Error('Start date or end date not set!');
    }

    const rowStartDateMs = this.startDate?.getTime();
    const rowEndDateMs = this.endDate?.getTime();

    return data.map((dataElement) => {
      const segmentStartDateMs = new Date(dataElement.start).getTime();
      const segmentEndDateMs = dataElement.end
        ? new Date(dataElement.end).getTime()
        : segmentStartDateMs;
      const end = this.calculatePercentage(
        segmentEndDateMs,
        rowStartDateMs,
        rowEndDateMs,
      );
      const left = this.calculatePercentage(
        segmentStartDateMs,
        rowStartDateMs,
        rowEndDateMs,
      );
      const width = end - left;
      return {
        dataTypes: dataElement.dataTypes,
        left: left,
        width: width,
        hint: dataElement.hint,
        styles: dataElement.styles,
        startTime: new Date(dataElement.start),
      };
    });
  }

  private calculatePercentage(
    itemStart: number,
    rowStart: number,
    rowEnd: number,
  ): number {
    return ((itemStart - rowStart) / (rowEnd - rowStart)) * 100;
  }

  private setWidthOfTimelineGrid() {
    const el = this.el.nativeElement.querySelector(
      '.timeline__content__row-container',
    );
    this.widthOfTimelineGrid =
      el.offsetWidth -
      this.widthToEndOfDataHeadersInGridElement -
      this.marginOfTimelineGridElement;
  }

  private findHoverTime(x: number): number {
    const percentOfWidth = x / this.widthOfTimelineGrid;
    const dif = this.endDate!.getTime() - this.startDate!.getTime();
    const numMSAfterStart = dif * percentOfWidth;
    const ms = numMSAfterStart + this.startDate!.getTime();
    return ms;
  }
}

export interface IPlaceable {
  dataTypes: string[];
  left: number;
  width: number;
  hint: string | undefined;
  styles: string | undefined;
}

export interface ITimelineData {
  dataTypes: string[];
  start: Date;
  end: Date | undefined;
  hint?: string;
  styles?: string;
}
